/* eslint-disable no-nested-ternary */
import React, { useContext, useState } from 'react';
import { CachePolicies, useFetch } from 'use-http';
import useAsyncEffect from 'use-async-effect';
import styled from 'styled-components';
import { StringParam, useQueryParam } from 'use-query-params';
import { Experience } from '@/webapi/use-experience-api';
import { Opt } from '@/features/details/date-pick/quick-range-select';
import { routes } from '@/webapi/routes';
import { ExpVersion } from '@/webapi/use-analytics-api';
import { Card } from '@/components/card';
import { IconButton } from '@/features/dashboard/reports_v2/common';
import { MinusSvg } from '@/features/dashboard/reports_v2/icons/minusSvg';
import { PlusSvg } from '@/features/dashboard/reports_v2/icons/plusSvg';
import { maybe } from '@/features/details/utils';
import { MetricKind } from '@/webapi/models';
import { DatePickerContext } from '@/features/details/date-pick/context';
import { useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';
import { breakpoints } from '@/components/responsive';

export interface Uplift {
  useCase: string;
  cr: number;
  aov: number;
  psv: number;
  runningDays: number;
  revenueViaExp: number;
  storeRevenue: number;
}
export function WinnerUplift({ experience }: { experience: Experience }) {
  const { versionsSelect, version, setVersion, versions, setStep } =
    useContext(DatePickerContext);
  const abTest = findAbTest(experience, version?.version);
  const expVersion = findSelectedVersion(
    experience,
    maybe(() => Math.min(abTest.version + 1, version.version)),
  );
  const winnerVariant = findWinnerVariant(expVersion);

  const isUpliftStatsEnabled = useFeatureBit(FeatureBit.UPLIFT_METRICS);
  const { post, loading } = useFetch(``, (globalOptions) => ({
    ...globalOptions,
    ...{
      credentials: `include`,
      cachePolicy: CachePolicies.NO_CACHE,
      loading: true,
    },
  }));

  const [uplift, setUplift] = useState<Uplift | undefined>();
  const [open, setOpen] = useState(true);
  const [metric, setMetric] = useState(``);

  const [sessionsType] = useQueryParam(`st`, StringParam);
  useAsyncEffect(async () => {
    if (!abTest?.version || !isUpliftStatsEnabled || !experience?.id) return;
    const resp: {
      uplifts: Uplift[];
    } = await post(routes.experienceUplift(), {
      useCaseVersions: { [experience?.id]: abTest?.version },
      sessionType: sessionsType,
      goals: {
        [experience.id]: experience?.primaryGoal || MetricKind.CONVERSION_RATE,
      },
      winingVariants: {
        [experience.id]: winnerVariant,
      },
    });
    if (resp?.uplifts?.length > 0) {
      const lift = resp?.uplifts?.[0];
      setUplift(lift);
      if (experience.primaryGoal === MetricKind.PER_SESSION_VALUE) {
        setMetric(`psv`);
      } else if (experience.primaryGoal === MetricKind.AVG_ORDER_VALUE) {
        setMetric(`aov`);
      } else {
        setMetric(`cr`);
      }
    }
  }, [abTest?.version, sessionsType]);
  if (!winnerVariant || loading || !abTest?.version || !experience?.id) {
    return null;
  }
  if (!metric) {
    return null;
  }
  if (uplift?.runningDays < 3 || experience.hideUplift) {
    return null;
  }
  const incDec = uplift?.[metric] < 0 ? `decreased` : `increased`;
  return (
    <DetailsCard open={open} onClick={() => !open && setOpen(!open)}>
      <Title open={open}>
        Experience Summary{` `}
        {` (${
          metric === `cr`
            ? `Conversion Rate`
            : metric === `aov`
            ? `Average Order Value`
            : `Per Session Value`
        })`}
        <Controls>
          {open && (
            <>
              <IconButton onClick={() => setMetric(`cr`)}>
                <MetricBtn active={metric === `cr`}>CR</MetricBtn>
              </IconButton>
              <IconButton onClick={() => setMetric(`aov`)}>
                <MetricBtn active={metric === `aov`}>AOV</MetricBtn>
              </IconButton>
              <IconButton onClick={() => setMetric(`psv`)}>
                <MetricBtn active={metric === `psv`}>PSV</MetricBtn>
              </IconButton>
            </>
          )}
          <IconButton onClick={() => setOpen(!open)}>
            {open ? <MinusSvg /> : <PlusSvg />}
          </IconButton>
        </Controls>
      </Title>
      {open && (
        <>
          {metric === `cr` && (
            <Description>
              In the latest A/B test (run for {uplift?.runningDays || `...`}
              {` `}
              days),{` `}
              <span style={{ color: `black` }}>
                the new variation {incDec} the conversion rate (CR) by{` `}
                {uplift?.cr?.toFixed(2)}%{` `}
              </span>
              before the experience was fully implemented.
              <br /> As a result, the overall conversion rate (across all
              devices and visitors){` `}
              <span style={{ color: `black` }}>
                {incDec} by approximately{` `}
                {maybe(
                  () =>
                    (
                      uplift.cr *
                      (uplift.revenueViaExp / uplift.storeRevenue)
                    ).toFixed(2),
                  `0`,
                )}
                %
              </span>
            </Description>
          )}
          {metric === `aov` && (
            <Description>
              In the latest A/B test (run for {uplift.runningDays} days),{` `}
              <span style={{ color: `black` }}>
                the new variation {incDec} the average order value (AOV) by
                {` `}
                {uplift?.aov?.toFixed(2)}%{` `}
              </span>
              before the experience was fully implemented.
              <br /> As a result, the overall average order value (across all
              devices and visitors){` `}
              <span style={{ color: `black` }}>
                {incDec} by approximately{` `}
                {maybe(
                  () =>
                    (
                      uplift.aov *
                      (uplift.revenueViaExp / uplift.storeRevenue)
                    ).toFixed(2),
                  `0`,
                )}
                %
              </span>
            </Description>
          )}
          {metric === `psv` && (
            <Description>
              In the latest A/B test (run for {uplift.runningDays} days),{` `}
              <span style={{ color: `black` }}>
                the new variation {incDec} the per session value (PSV) by
                {` `}
                {uplift?.psv?.toFixed(2)}%{` `}
              </span>
              before the experience was fully implemented.
              <br /> As a result, the overall per session value (across all
              devices and visitors){` `}
              <span style={{ color: `black` }}>
                {incDec} by approximately{` `}
                {maybe(
                  () =>
                    (
                      uplift.psv *
                      (uplift.revenueViaExp / uplift.storeRevenue)
                    ).toFixed(2),
                  `0`,
                )}
                %
              </span>
            </Description>
          )}
        </>
      )}
      {open && (
        <LinkToReport
          onClick={() => {
            setTimeout(() => {
              setVersion(versions.find((v) => v.version === abTest.version));
              setStep(Opt.PreviousVersion);
              versionsSelect();
            }, 500);
          }}
        >
          See the latest version of the report
        </LinkToReport>
      )}
    </DetailsCard>
  );
}
const MetricBtn = styled.span`
  font-family: Inter, serif;
  color: ${(p) => (p.active ? `dark-grey` : `#868686`)};
  font-weight: 700;
  &:hover {
    color: black;
  }
`;
const Controls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;
const LinkToReport = styled.span`
  font-family: 'Inter', serif;
  font-weight: 400;
  font-size: 1.6rem;
  text-decoration: underline;
  &:hover {
    color: black;
    cursor: pointer;
  }
  color: #868686;
`;

const DetailsCard = styled(Card)`
  && {
    width: 100%;
    margin-bottom: 2rem;
    cursor: default;
    display: grid;
    grid-row-gap: 1rem;
    padding: ${(p) => (p.open ? `2rem 3.5rem 3.5rem 3.5rem` : `0.5rem 3.5rem`)};
    border-radius: 2rem;
    &:hover {
      ${(p) => !p.open && `opacity: 0.5`};
      ${(p) => !p.open && `cursor: pointer;`};
    }

    ${breakpoints.down(`md`)} {
      display: none;
    }
  }
`;
const Title = styled.span`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: 'Inter', serif;
  font-weight: 700;
  font-size: 1.8rem;
  ${(p) => p.open && `padding: 1rem 0;`};
  ${(p) => !p.open && `margin: 2rem 0;`};
`;
const Description = styled.p`
  font-family: 'Inter', serif;
  font-weight: 500;
  font-size: 1.4rem;
  line-height: 3.5rem;
  margin-top: 0;
  color: #868686;
`;

const findAbTest = (experience: Experience, version: number) =>
  experience?.versions
    ?.filter(
      (v) =>
        v.version < version &&
        !Object.values(v?.variantChances || {}).includes(100),
    )
    ?.reduce((p, c) => (p.version > c.version ? p : c), { version: 0 });
const findSelectedVersion = (experience: Experience, version: number) =>
  experience?.versions?.find((v) => v.version === version) ||
  experience.currentVersion;

const findWinnerVariant = (expVersion: ExpVersion) => {
  const [winnerVariant] =
    Object.entries(expVersion?.variantChances || {})?.find(
      ([_, chance]) => chance === 100,
    ) || [];
  return winnerVariant;
};
